import React, { useState, useEffect } from "react";
import Page from "../../components/Page";
import SettingsNavbar from "../../components/SettingsNavbar";
import {
  IconKey,
  IconLock,
  IconCheck,
  IconX,
  IconRefresh,
  IconDeviceFloppy,
  IconPlugConnected,
  IconBuildingStore,
  IconBluetooth,
  IconWifi,
  IconUsb,
  IconShieldLock,
  IconAlertTriangle,
  IconInfoCircle,
  IconArrowRight,
  IconLink
} from "@tabler/icons-react";
import { iconStroke } from "../../config/config";
import { useRfidConfig, saveRfidConfig, testRfidConnection, syncRfidLocks } from "../../controllers/rfid.controller";
import toast from "react-hot-toast";
import { useTheme } from "../../contexts/ThemeContext";
import { Link } from "react-router-dom";

export default function RfidSettingsPage() {
  const { theme } = useTheme();
  const { data: config, error, mutate, isLoading } = useRfidConfig();

  const [activeTab, setActiveTab] = useState("general");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Form states
  const [isEnabled, setIsEnabled] = useState(false);
  const [provider, setProvider] = useState("sciener");
  const [apiBaseUrl, setApiBaseUrl] = useState("https://api.sciener.com");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [accountUsername, setAccountUsername] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [defaultMethod, setDefaultMethod] = useState("encoder");
  const [allowedMethods, setAllowedMethods] = useState(["bluetooth", "cloud", "encoder"]);
  const [cardSector, setCardSector] = useState("1");
  const [encoderComPort, setEncoderComPort] = useState("COM1");
  const [encoderBridgeUrl, setEncoderBridgeUrl] = useState("http://127.0.0.1:8989");
  const [buildingNumber, setBuildingNumber] = useState(1);
  const [floorCount, setFloorCount] = useState(1);
  const [cardValidityHours, setCardValidityHours] = useState(24);
  const [lostCardPolicy, setLostCardPolicy] = useState("cancel_and_reissue");
  const [checkoutRevocation, setCheckoutRevocation] = useState(true);
  const [allowStaffCards, setAllowStaffCards] = useState(false);

  useEffect(() => {
    if (config) {
      setIsEnabled(!!config.is_enabled);
      setProvider(config.provider || "sciener");
      setApiBaseUrl(config.api_base_url || "https://api.sciener.com");
      setClientId(config.client_id || "");
      setClientSecret(config.client_secret || "");
      setAccountUsername(config.account_username || "");
      setAccountPassword(config.account_password || "");
      setDefaultMethod(config.default_card_method || "encoder");
      if (config.allowed_methods) {
        setAllowedMethods(config.allowed_methods.split(",").map(m => m.trim()));
      }
      setCardSector(config.card_sector || "1");
      setEncoderComPort(config.encoder_com_port || "COM1");
      setEncoderBridgeUrl(config.encoder_bridge_url || "http://127.0.0.1:8989");
      setBuildingNumber(config.building_number || 1);
      setFloorCount(config.floor_count || 1);
      setCardValidityHours(config.card_validity_hours || 24);
      setLostCardPolicy(config.lost_card_policy || "cancel_and_reissue");
      setCheckoutRevocation(config.checkout_revocation === 1);
      setAllowStaffCards(config.allow_staff_cards === 1);
    }
  }, [config]);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      await saveRfidConfig({
        is_enabled: isEnabled ? 1 : 0,
        provider,
        api_base_url: apiBaseUrl,
        client_id: clientId,
        client_secret: clientSecret,
        account_username: accountUsername,
        account_password: accountPassword,
        default_card_method: defaultMethod,
        allowed_methods: allowedMethods.join(","),
        card_sector: cardSector,
        encoder_com_port: encoderComPort,
        encoder_bridge_url: encoderBridgeUrl,
        building_number: buildingNumber,
        floor_count: floorCount,
        card_validity_hours: cardValidityHours,
        lost_card_policy: lostCardPolicy,
        checkout_revocation: checkoutRevocation ? 1 : 0,
        allow_staff_cards: allowStaffCards ? 1 : 0,
      });
      toast.success("RFID Door Lock settings saved successfully!");
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await testRfidConnection({
        api_base_url: apiBaseUrl,
        client_id: clientId,
        client_secret: clientSecret,
        account_username: accountUsername,
        account_password: accountPassword,
      });
      toast.success(res.data?.message || "Connection test succeeded!");
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const handleSyncLocks = async () => {
    setSyncing(true);
    try {
      const res = await syncRfidLocks();
      toast.success(res.data?.message || "Locks synchronized successfully!");
      mutate();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Failed to sync locks");
    } finally {
      setSyncing(false);
    }
  };

  const toggleAllowedMethod = (m) => {
    if (allowedMethods.includes(m)) {
      if (allowedMethods.length === 1) {
        toast.error("At least one issuing method must remain allowed");
        return;
      }
      setAllowedMethods(allowedMethods.filter(x => x !== m));
    } else {
      setAllowedMethods([...allowedMethods, m]);
    }
  };

  const tabs = [
    { id: "general", label: "General", icon: <IconBuildingStore stroke={iconStroke} size={18} /> },
    { id: "api", label: "Sciener / TTHotel API", icon: <IconPlugConnected stroke={iconStroke} size={18} /> },
    { id: "mapping", label: "Lock & Room Mapping", icon: <IconLink stroke={iconStroke} size={18} /> },
    { id: "bluetooth", label: "Bluetooth Issuing", icon: <IconBluetooth stroke={iconStroke} size={18} /> },
    { id: "cloud", label: "Cloud / Gateway", icon: <IconWifi stroke={iconStroke} size={18} /> },
    { id: "encoder", label: "USB Card Encoder", icon: <IconUsb stroke={iconStroke} size={18} /> },
    { id: "policies", label: "Card Policies", icon: <IconShieldLock stroke={iconStroke} size={18} /> },
  ];

  return (
    <Page className="px-4 py-3 flex gap-4 min-h-screen">
      <SettingsNavbar />

      <div className="flex-1 max-w-5xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between pb-4 border-b border-gray-200 dark:border-neutral-700 gap-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-xl">
                <IconKey stroke={iconStroke} size={24} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  RFID Door Lock Integration
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Sciener / TTLock / TTHotel Smart Lock Management & IC Card Issuance
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to="/dashboard/rfid-doorlock"
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-800 text-gray-700 dark:text-gray-200 flex items-center gap-1.5"
            >
              <span>RFID Dashboard</span>
              <IconArrowRight size={14} />
            </Link>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5 shadow-sm transition disabled:opacity-50"
            >
              <IconDeviceFloppy size={16} />
              <span>{saving ? "Saving..." : "Save Changes"}</span>
            </button>
          </div>
        </div>

        {/* Status banner */}
        <div className="mt-4 p-3 rounded-xl border flex items-center justify-between gap-3 bg-white dark:bg-neutral-800 border-gray-200 dark:border-neutral-700">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${config?.connection_status === 'connected' ? 'bg-emerald-500 animate-pulse' : config?.connection_status === 'error' ? 'bg-rose-500' : 'bg-amber-500'}`} />
            <div>
              <div className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                Status: {config?.connection_status || "Not Configured"}
              </div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">
                {config?.last_connection_test ? `Last tested: ${new Date(config.last_connection_test).toLocaleString()}` : "No connection test run yet"}
                {config?.last_sync_at ? ` • Last synced: ${new Date(config.last_sync_at).toLocaleString()}` : ""}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleTestConnection}
              disabled={testing}
              className="px-3 py-1 text-xs rounded-md border border-gray-300 dark:border-neutral-600 hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-700 dark:text-gray-200 flex items-center gap-1 disabled:opacity-50"
            >
              <IconPlugConnected size={14} />
              <span>{testing ? "Testing..." : "Test Connection"}</span>
            </button>
            <button
              onClick={handleSyncLocks}
              disabled={syncing}
              className="px-3 py-1 text-xs rounded-md bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-900/40 hover:bg-blue-100 flex items-center gap-1 disabled:opacity-50"
            >
              <IconRefresh size={14} className={syncing ? "animate-spin" : ""} />
              <span>{syncing ? "Syncing..." : "Sync Locks"}</span>
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-4 flex flex-wrap border-b border-gray-200 dark:border-neutral-700 gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 border-b-2 transition ${
                activeTab === tab.id
                  ? "border-emerald-600 text-emerald-600 dark:border-emerald-400 dark:text-emerald-400"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="mt-4 bg-white dark:bg-neutral-800 rounded-2xl border border-gray-200 dark:border-neutral-700 p-5 shadow-sm">
          {/* 1. GENERAL TAB */}
          {activeTab === "general" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-neutral-900/50 border border-gray-200 dark:border-neutral-700">
                <div>
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    Enable RFID Door Lock Integration
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Activate smart lock communication, IC card issuance, and front-desk check-in/checkout integration.
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={(e) => setIsEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600" />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Provider
                  </label>
                  <select
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200"
                  >
                    <option value="sciener">Sciener / TTLock / TTHotel Open Platform</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    API Region / Base URL
                  </label>
                  <select
                    value={apiBaseUrl}
                    onChange={(e) => setApiBaseUrl(e.target.value)}
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200"
                  >
                    <option value="https://api.sciener.com">Global / US (api.sciener.com)</option>
                    <option value="https://euapi.sciener.com">Europe (euapi.sciener.com)</option>
                    <option value="https://cnapi.sciener.com">China (cnapi.sciener.com)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Default Card Issuing Method
                  </label>
                  <select
                    value={defaultMethod}
                    onChange={(e) => setDefaultMethod(e.target.value)}
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200"
                  >
                    <option value="encoder">USB Card Encoder (Reception Station)</option>
                    <option value="cloud">Cloud / Gateway (Over the Air)</option>
                    <option value="bluetooth">Bluetooth Mobile SDK (Staff App)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Hotel Building Number
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={buildingNumber}
                    onChange={(e) => setBuildingNumber(parseInt(e.target.value) || 1)}
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200"
                  />
                </div>
              </div>
            </div>
          )}

          {/* 2. SCIENER / TTHOTEL API TAB */}
          {activeTab === "api" && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/40 rounded-xl text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
                <IconInfoCircle size={18} className="shrink-0 mt-0.5" />
                <div>
                  All API secrets and passwords are encrypted using AES-256-CBC and kept exclusively on the server. Credentials are never sent back in plaintext or exposed in browser logs.
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Sciener Client ID (App ID) *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. 5f483c6b84..."
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Sciener Client Secret *
                  </label>
                  <input
                    type="password"
                    placeholder={config?.client_secret ? "••••••••••••••••" : "Enter Client Secret"}
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Hotel Account Username / Email *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. hotel_manager@hotel.com"
                    value={accountUsername}
                    onChange={(e) => setAccountUsername(e.target.value)}
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Hotel Account Password *
                  </label>
                  <input
                    type="password"
                    placeholder={config?.has_credentials ? "••••••••" : "Enter Account Password"}
                    value={accountPassword}
                    onChange={(e) => setAccountPassword(e.target.value)}
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200"
                  />
                </div>
              </div>

              {config?.last_error && (
                <div className="p-3 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/40 rounded-xl text-xs text-rose-700 dark:text-rose-300 flex items-start gap-2">
                  <IconAlertTriangle size={18} className="shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold">Last Error:</span> {config.last_error}
                  </div>
                </div>
              )}

              <div className="pt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={testing}
                  className="px-4 py-2 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                >
                  <IconPlugConnected size={16} />
                  <span>{testing ? "Testing Credentials..." : "Test Connection Now"}</span>
                </button>
              </div>
            </div>
          )}

          {/* 3. LOCK & ROOM MAPPING TAB */}
          {activeTab === "mapping" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-neutral-900/50 border border-gray-200 dark:border-neutral-700">
                <div>
                  <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    Lock to Room Mapping Manager
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Map synced Sciener locks to your hotel rooms. Enforces 1:1 unique room mappings.
                  </div>
                </div>
                <Link
                  to="/dashboard/rfid-doorlock/mapping"
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5"
                >
                  <IconLink size={16} />
                  <span>Open Mapping Screen</span>
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Hotel Card Sector
                  </label>
                  <input
                    type="text"
                    value={cardSector}
                    onChange={(e) => setCardSector(e.target.value)}
                    placeholder="Default: 1"
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200 font-mono"
                  />
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                    MIFARE card sector index used for hotel room keys (1-15).
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Total Hotel Floors
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={floorCount}
                    onChange={(e) => setFloorCount(parseInt(e.target.value) || 1)}
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200"
                  />
                </div>
              </div>
            </div>
          )}

          {/* 4. BLUETOOTH ISSUING TAB */}
          {activeTab === "bluetooth" && (
            <div className="space-y-4">
              <div className="p-3 bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900/40 rounded-xl text-xs text-indigo-700 dark:text-indigo-300 flex items-start gap-2">
                <IconBluetooth size={20} className="shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold">Official Sciener Mobile SDK Requirement</div>
                  <div>
                    Direct Bluetooth Low Energy (BLE) card writing and lock communication cannot be performed directly from a standard web browser. Staff members use the eHospitality Mobile App (equipped with the official Sciener BLE SDK) while standing in proximity to the lock.
                  </div>
                </div>
              </div>

              <div className="p-3 rounded-xl bg-gray-50 dark:bg-neutral-900/50 border border-gray-200 dark:border-neutral-700 space-y-2">
                <div className="text-xs font-bold text-gray-800 dark:text-gray-200">
                  Bluetooth Card Issuing Flow:
                </div>
                <ol className="list-decimal list-inside text-xs text-gray-600 dark:text-gray-400 space-y-1">
                  <li>Front-desk selects "Bluetooth Mobile SDK" during card issue.</li>
                  <li>Card issuance order is created in pending state with encrypted lock data.</li>
                  <li>Staff opens Mobile App near the lock and taps "Write Card to Lock".</li>
                  <li>Mobile app connects to lock via BLE and confirms IC card registration.</li>
                </ol>
              </div>
            </div>
          )}

          {/* 5. CLOUD / GATEWAY TAB */}
          {activeTab === "cloud" && (
            <div className="space-y-4">
              <div className="p-3 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/40 rounded-xl text-xs text-emerald-700 dark:text-emerald-300 flex items-start gap-2">
                <IconWifi size={20} className="shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold">Wi-Fi Gateway Cloud Operations</div>
                  <div>
                    Locks with a linked G2/G3 Wi-Fi Gateway support instant over-the-air card issuance and remote card cancellation directly from the eHospitality front-desk without physical proximity.
                  </div>
                </div>
              </div>

              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
                <p>
                  To issue a card via Gateway:
                </p>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li>Connect any standard USB RFID Reader to read the card UID into the Card Number field.</li>
                  <li>Front-desk submits the card; the cloud API registers the card directly on the gateway-connected lock.</li>
                  <li>Card becomes immediately active on the door.</li>
                </ul>
              </div>
            </div>
          )}

          {/* 6. USB CARD ENCODER TAB */}
          {activeTab === "encoder" && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/40 rounded-xl text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2">
                <IconUsb size={20} className="shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold">Local Windows Card Encoder Bridge</div>
                  <div>
                    For offline hotel locks, the TTHotel USB Card Encoder writes encrypted building, floor, room, and validity timestamps directly into the MIFARE card sectors. The eHospitality Local Bridge service communicates with the Windows Card Encoder DLL.
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Encoder COM Port
                  </label>
                  <input
                    type="text"
                    value={encoderComPort}
                    onChange={(e) => setEncoderComPort(e.target.value)}
                    placeholder="e.g. COM1, COM3"
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Encoder Local Bridge Service URL
                  </label>
                  <input
                    type="text"
                    value={encoderBridgeUrl}
                    onChange={(e) => setEncoderBridgeUrl(e.target.value)}
                    placeholder="http://127.0.0.1:8989"
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200 font-mono"
                  />
                </div>
              </div>
            </div>
          )}

          {/* 7. CARD POLICIES TAB */}
          {activeTab === "policies" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Allowed Issuing Methods for this Hotel
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[
                    { id: "encoder", name: "USB Card Encoder", desc: "Reception desk encoder writes" },
                    { id: "cloud", name: "Cloud / Gateway", desc: "Over-the-air card issue to Wi-Fi locks" },
                    { id: "bluetooth", name: "Bluetooth Mobile SDK", desc: "Staff mobile BLE write" },
                  ].map((m) => (
                    <div
                      key={m.id}
                      onClick={() => toggleAllowedMethod(m.id)}
                      className={`p-3 rounded-xl border cursor-pointer transition ${
                        allowedMethods.includes(m.id)
                          ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20"
                          : "border-gray-200 dark:border-neutral-700 opacity-60"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-800 dark:text-gray-200">{m.name}</span>
                        {allowedMethods.includes(m.id) && <IconCheck size={16} className="text-emerald-600" />}
                      </div>
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{m.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Default Guest Card Validity (Hours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={cardValidityHours}
                    onChange={(e) => setCardValidityHours(parseInt(e.target.value) || 24)}
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200"
                  />
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                    Used when exact checkout time is not specified in reservation.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Lost Card Policy
                  </label>
                  <select
                    value={lostCardPolicy}
                    onChange={(e) => setLostCardPolicy(e.target.value)}
                    className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-gray-800 dark:text-gray-200"
                  >
                    <option value="cancel_and_reissue">Revoke Old Card & Issue Replacement</option>
                    <option value="reissue_only">Issue Replacement Only</option>
                  </select>
                </div>
              </div>

              <div className="pt-2 space-y-3">
                <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-neutral-900/50 border border-gray-200 dark:border-neutral-700">
                  <div>
                    <div className="text-xs font-bold text-gray-800 dark:text-gray-200">
                      Automatic Card Revocation on Checkout
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400">
                      Attempt instant revocation of all active guest RFID cards when checkout is processed.
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={checkoutRevocation}
                    onChange={(e) => setCheckoutRevocation(e.target.checked)}
                    className="w-4 h-4 accent-emerald-600 rounded"
                  />
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-neutral-900/50 border border-gray-200 dark:border-neutral-700">
                  <div>
                    <div className="text-xs font-bold text-gray-800 dark:text-gray-200">
                      Allow Master & Staff Cards
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400">
                      Enable issuing housekeeping and master pass cards across multiple doors.
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={allowStaffCards}
                    onChange={(e) => setAllowStaffCards(e.target.checked)}
                    className="w-4 h-4 accent-emerald-600 rounded"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Page>
  );
}
